// VerifyModal.jsx
import { useRef, useState, useEffect } from "react";
import {
    Dialog,
    Paper,
    IconButton,
    Typography,
    TextField,
    Stack,
    Button,
    Box,
    OutlinedInput
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useTheme } from "@mui/material/styles";
import { modelStyles } from "../Styles/modelStyles";
import { pageStyles } from "../Styles/pageStyles";
import BorderColorIcon from '@mui/icons-material/BorderColor';
import { AuthServices } from "../Services/authServices";
import { apiHandler } from "@shared/Functions/apiHandler";
import { toast } from "sonner";

export default function VerifyModal({
    open,
    onClose,
    email,
    onVerify,
    Status,
    setData,
}) {
    const [code, setCode] = useState(new Array(6).fill(""));
    const theme = useTheme();
    const classes = modelStyles(theme);
    const pageSty = pageStyles(theme);
    const inputsRef = useRef([]);
    const [editStatus, setEditStatus] = useState(true);
    const [previousData, setPreviousData] = useState("");
    const [timer, setTimer] = useState(0);
    const [isDisabled, setIsDisabled] = useState(false);

    const focusInput = (index) => {
        const el = inputsRef.current[index];
        el?.focus();
        el?.select?.();
    };

    const handleChange = (index, value) => {
        if (!/^\d?$/.test(value)) return;
        const next = [...code];
        next[index] = value;
        setCode(next);
        if (value && index < 5) focusInput(index + 1);
    };

    const handleKeyDown = (e, index) => {
        if (e.key === "Backspace") {
            if (code[index]) {
                const next = [...code];
                next[index] = "";
                setCode(next);
            } else if (index > 0) {
                focusInput(index - 1);
                const next = [...code];
                next[index - 1] = "";
                setCode(next);
            }
        }
    };

    const handlePaste = (e) => {
        const paste = (e.clipboardData || window.clipboardData).getData("text");
        const digits = paste.replace(/\D/g, "").slice(0, 6).split("");
        if (digits.length === 0) return;
        const next = [...code];
        for (let i = 0; i < 6; i++) {
            next[i] = digits[i] ?? "";
        }
        setCode(next);
        const firstEmpty = next.findIndex((c) => c === "");
        focusInput(firstEmpty === -1 ? 5 : firstEmpty);
        e.preventDefault();
    };

    const submit = () => {
        const joined = code.join("");
        if (joined.length < 6) {
            toast.error("Please enter a valid code");
            return;
        }
        onVerify?.(joined);
    };

    const changeEdit = () => {
        setEditStatus(!editStatus)
    }

    const changeEmailPhone = async () => {
        let payload = {
            "oldEmailOrPhone": previousData,
            "emailOrPhone": email,
            "type": Status // 1 - Email, 2 - Phone,
        }
        console.log(payload, "payload");
        const res = await AuthServices.changeEmailOrPhone(payload);
        apiHandler(res, classes.successToast, true);
        if (res?.status === 200) {
            console.log(res?.data?.data?.emailOrPhone, "res");
            setPreviousData(res?.data?.data?.emailOrPhone);
        }
    }

    const sendNewCode = async () => {
        if (isDisabled) return; // prevent multiple clicks

        let email = sessionStorage.getItem("email");
        let payload = {
            emailOrPhone: email,
            type: 1,
        };

        console.log(payload, "payload");

        setIsDisabled(true);
        setTimer(60); // start 60s countdown


        const res = await AuthServices.checkCustomer(payload);
        apiHandler(res, classes.successToast, true);

    };

    // Countdown logic
    useEffect(() => {
        let interval;
        if (isDisabled && timer > 0) {
            interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
        } else if (timer === 0) {
            setIsDisabled(false);
            clearInterval(interval);
        }
        return () => clearInterval(interval);
    }, [isDisabled, timer]);


    useEffect(() => {
        let phoneNo = sessionStorage.getItem("phoneNo");
        let email = sessionStorage.getItem("email");
        if (Status === 1) {
            setPreviousData(email);
        } else {
            setPreviousData(phoneNo);
        }
    }, [open]);

    return (
        <Dialog
            open={open}
            onClose={(event, reason) => {
                if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
                    onClose?.();
                }
            }}
            maxWidth="xs"
            fullWidth
        >

            <Paper sx={classes.mainDiv}>
                <Box sx={classes.flex_start}>

                    <IconButton
                        onClick={onClose}
                        size="small"
                        aria-label="back"
                    >
                        <ArrowBackIcon />
                    </IconButton>

                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Verify {Status === 1 ? "Email" : "Phone Number"}
                    </Typography>
                </Box>

                <Typography sx={pageSty.darkTextMedium}>
                    We have sent you a 6 digit code. Please enter here to verify your {Status === 1 ? "Email" : "Phone Number"}.
                </Typography>

                <OutlinedInput
                    size="small"
                    fullWidth
                    disabled={editStatus}
                    value={email}
                    sx={{ ...pageSty.inputField, my: 1 }}

                    onChange={(e) =>
                        setData((prevState) => ({
                            ...prevState,
                            [Status === 1 ? "email" : "phone"]: e.target.value,
                        }))
                    }

                    endAdornment={
                        editStatus ? (
                            <BorderColorIcon
                                onClick={changeEdit}
                                sx={{ color: "warning.main", cursor: "pointer" }}
                            />
                        ) : (
                            <Typography
                                sx={{ color: "warning.main", cursor: "pointer", fontWeight: "bold" }}
                                onClick={changeEmailPhone}
                            >
                                Change
                            </Typography>
                        )
                    }

                />

                <Typography sx={pageSty.darkTextMedium} my={1}>
                    Enter 6 Digits Code
                </Typography>

                <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
                    {code.map((c, i) => (
                        <TextField
                            key={i}
                            inputRef={(el) => (inputsRef.current[i] = el)}
                            value={c}
                            onChange={(e) => handleChange(i, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(e, i)}
                            onPaste={handlePaste}
                            placeholder="0"
                            inputProps={{
                                maxLength: 1,
                                style: { textAlign: "center", fontSize: 16, padding: "10px 8px" },
                                "aria-label": `digit-${i + 1}`,
                            }}
                            size="small"
                            sx={pageSty.OtpInput}
                        />
                    ))}
                </Stack>

                <Typography sx={pageSty.darkTextMedium} my={1}>
                    Didn't Receive Code?{" "}
                    <Box
                        component="span"
                        sx={{
                            color: isDisabled ? "text.disabled" : "warning.main",
                            fontWeight: 600,
                            cursor: isDisabled ? "not-allowed" : "pointer",
                            pointerEvents: isDisabled ? "none" : "auto",
                        }}
                        onClick={sendNewCode}
                    >
                        {isDisabled ? `Resend in ${timer}s` : "Get a New One"}
                    </Box>
                </Typography>

                <Button
                    variant="contained"
                    fullWidth
                    onClick={submit}
                    sx={{ my: 1, borderRadius: 3, py: 1 }}>
                    Verify and Continue
                </Button>
            </Paper>
        </Dialog>
    );
}
